import { Injectable } from '@angular/core';

import { firstValueFrom, Observable } from 'rxjs';

import { BaseService } from 'app/_services/base.service';
import { SettingsService } from 'app/_services/settings.service';

import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { UserLoginResponse } from './login/_models/userLoginResponse';
import { UserMultiloginData } from 'app/_models/userMultiloginData';
import { Router } from '@angular/router';
import { UserService } from 'app/_services/user.service';
import { UserCashbackData } from 'app/_models/userCashbackData';
import { environment } from '../../environments/environment';
import { ScriptService } from 'app/_services/script.service';
import { NotificationService } from 'app/_services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { SignalRService } from 'app/_services/signalr.service';
import { MixPanelEventEnum } from 'app/_models/mixPanelEventEnum';
import { MixPanelService } from 'app/_services/mix-panel.service';
import { SweetAlertParams } from 'app/_models/notificationParams.model';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { UserAllowedPage } from 'app/_models/userAllowedPage';

declare const isEmpty: any;
declare const crisp: any;

@Injectable({ providedIn: 'root' })
export class AccountService extends BaseService {
    private _apiBuyerUrl: string = `${environment.apiBuyer}`;
    private _accountDomain: string = '/account';

    returnUrl: string;

    constructor(private userService: UserService, private mixPanelService: MixPanelService, private signalRService: SignalRService, private notificationService: NotificationService, private scriptService: ScriptService, private router: Router, private config: SettingsService, private translate: TranslateService, private ngxLoader: NgxUiLoaderService) {
        super();
    }

    public acceptTermsOfUser() {
        return this.config.executePost({ url: `${this._accountDomain}/accept-terms-of-use`, body: { user_ip: this.userService.user().user_ip }});
    }

    public changePassword(password: string, newPassword: string) {
        const body = { password, newPassword};
        const url = '/change-password';
        return this.config.executePut({ url, body, baseUrl: this.config.baseAuthUserUrl, isJson: false });
    }

    async changeUser(userId: number, isLoginProcess: boolean = false): Promise<void> {
        if (!this.userService.user().isAdmin)
            return this.handleChangeNotAdminUser(userId);

        const selectedUser = this.userService.user().dadosMultiUsuario.find(u => u.id == userId);

        this.userService.setCashbackData(null);
        this.userService.setSelectedUser(selectedUser);
        this.handleGetUserCashback()

        if (isLoginProcess)
            return;

        const currentUrl = this.router.url;
        await this.router.navigateByUrl('/alterar-senha', { skipLocationChange: true });
        await this.router.navigateByUrl(currentUrl);
    }

    public getAllowedPages(): Observable<ApiResponseModel<UserAllowedPage[]>> {
        return this.config.executeGet({ baseUrl: this.config.baseAuthUrl, url: '/pages' });
    }

    public getCashbackInfo(userId: number) : Observable<ApiResponseModel<UserCashbackData>> {
        return this.config.executeGet({ url: `/users/${userId}/cashback` });
    }

    public getCssInIframe() {
        // Se não estiver no iframe, deve ter menu. Se estiver no iframe, não deve ter menu
        return this.checkIfNotInIframe() ? '' : 'without-menu';
    }

    public checkIfNotInIframe() {
        return (window.self === window.top);
    }

    public getUserERPName(buyerCompanyId?: number): Observable<any> {
        var buyerCompanyId = buyerCompanyId ?? this.userService.userCompanyId();

        return this.config.executeGet({ baseUrl: this._apiBuyerUrl, url: `/users/${buyerCompanyId}/erp` })
    }

    public getMultiloginData(): Observable<ApiResponseModel<UserMultiloginData[]>> {
        return this.config.executeGet({ baseUrl: this.config.baseAuthUrl , url: '/multilogin' });
    }

    public getTermsOfUseSwal(): SweetAlertParams {
        let tipo = this.userService.userIsBuyer() ? "/oficinas" : "/fornecedores";
        const urlTermsOfUse = this.router.serializeUrl(this.router.createUrlTree([`documentos/termos-uso/${tipo}`]));
        const urlPrivacyPolitics = this.router.serializeUrl(this.router.createUrlTree([`documentos/politica-privacidade/${tipo}`]));

        let html = `
            <div class="d-flex flex-direction-column align-items-center">
                <div class="d-flex" style="width: 290px">
                    <div class="form-check c-checkbox needsclick">
                        <label class="needsclick" style="line-height: 20px;">
                            <input type="checkbox" value="" class="needsclick" id="checkbox"/>
                            <span class="fa fa-check"></span>
                        </label>
                    </div>
                    Aceito os <a href="${urlTermsOfUse}" target='_blank' style="color: #FE5D15 !important; cursor: pointer; margin-left: 5px">Termos de Uso</a>
                </div>
                <div class="d-flex" style="width: 290px">
                    <div class="form-check c-checkbox needsclick">
                        <label class="needsclick" style="line-height: 20px;">
                            <input type="checkbox" value="" class="needsclick" id="checkbox2"/>
                            <span class="fa fa-check"></span>
                        </label>
                    </div>
                    Aceito a <a href="${urlPrivacyPolitics}" target='_blank' style="color: #FE5D15 !important; cursor: pointer; margin-left: 5px">Politíca de Privacidade</a>
                </div>
            </div>
        `;

        const params: SweetAlertParams = {
            allowEscapeKey: false,
            allowOutsideClick: false,
            bigSwall: true,
            isHtml: true,
            icon: 'warning',
            message: html,
            title: 'login.termos-uso.titulo',
            showCancelButton: false,
            showCloseButton: false,
            customClass: { validationMessage: 'swal-hidden', input: 'swal-hidden' },
            preConfirm: () => {
                this.notificationService.clearToastr();

                var checkbox1 = (document.getElementById('checkbox') as HTMLInputElement).checked;
                var checkbox2 = (document.getElementById('checkbox2') as HTMLInputElement).checked;

                if(checkbox1 && checkbox2)
                    return true;

                this.notificationService.showWarningToastr('login.termos-uso.messages.warning', { translateMessage: true });
                return false;
            }
        }

        return params;
    }

    public async handleLogin(loginResponse: UserLoginResponse, shouldRedirect = true): Promise<boolean> {
        this.userService.setUserStateFromLoginResponse(loginResponse);
        const user = this.userService.user();

        this.translate.use(user.culture);

        if (isEmpty(user.first_login_date) && !user.isAdmin)
            await this.handleTermsOfService();

        var allowedPagesSuccess = await this.handleGetAllowedPages();

        if (!allowedPagesSuccess) {
            this.logout();
            return false;
        }

        this.handleMultilogin();

        this.signalRService.inicializar();
        this.mixPanelService.updateUserPropertiesMixPanel(user).subscribe({ error: (e: any) => console.error(e) });
        this.mixPanelService.trackMixPanelEvent(MixPanelEventEnum.LogIn).subscribe({ error: (e: any) => console.error(e) });
        crisp.login(user);

        if (!shouldRedirect)
            return true;

        if (this.returnUrl)
            this.router.navigateByUrl(this.returnUrl);
        else
            this.router.navigateByUrl(this.userService.user().homePage);

        return true;
    }

    public login(username: string, password: string): Observable<ApiResponseModel<UserLoginResponse>> {
        return this.config.executePost({ baseUrl: this.config.baseAuthUrl, url: '/login', body: { username, password } });
    }

    public loginById(userId: number): Observable<ApiResponseModel<UserLoginResponse>> {
        return this.config.executePost({ baseUrl: this.config.baseAuthUrl, url: `/login/${userId}` });
    }

    public logout(ignoreRedirect: boolean = false) {
        this.config.executePost({ url: `${this._accountDomain}/logout` }).subscribe({
            next: () => {
                this.userService.logoutUser();
                this.scriptService.exitGoogleAnalyticsScript();

                if (!ignoreRedirect)
                    this.router.navigate(['/login']);
            },
            error: () => {
                this.notificationService.showErrorToastr('login.logout.erro.corpo', { translateMessage: true });
                return;
            }
        });
    }

    public rememberPassword(email: string, recaptcha: string) {
        var sitekey = this.config.getCaptchaData().secretkey;

        const body = { email, recaptcha, sitekey };
        const url = `${this._accountDomain}/remember-password`;
        return this.config.executePost({ url, body });
    }

    public saveFirstLogin() : Observable<ApiResponseModel<boolean>> {
        const user_ip = this.userService.user().user_ip;

        return this.config.executePost({ url: `${this._accountDomain}/save-first-login`, body: { user_ip } });
    }

    private handleChangeNotAdminUser(userId) {
        this.ngxLoader.startLoader('loader-principal');

        this.loginById(userId).subscribe({
            next: async (response: ApiResponseModel<UserLoginResponse>) => {
                const currentUrl = this.router.url;
                var success = await this.handleLogin(response.result, false);
                this.ngxLoader.stopLoader('loader-principal');

                if (!success) {
                    this.notificationService.showErrorToastr('login.change-user.erro.corpo', { translateMessage: true });
                    this.logout();
                    return;
                }

                const urlToNavigate =  this.userService.user().pages.some((p: UserAllowedPage) => currentUrl.includes(p.url)) ? currentUrl : this.userService.user().homePage;

                this.notificationService.showSuccessToastr('login.multi-usuario.mensagens.sucesso.corpo', { translateMessage: true });
                await this.router.navigateByUrl('/alterar-senha', { skipLocationChange: true });
                this.router.navigateByUrl(urlToNavigate);
            },
            error: () => {
                this.notificationService.showErrorToastr('login.change-user.erro.corpo', { translateMessage: true });
            }
        })
    }

    private handleGetUserCashback(): void {
        if (!this.userService.userIsBuyer() && !this.userService.userIsCentral())
            return;

        this.getCashbackInfo(this.userService.userId()).subscribe({
            next: (response: ApiResponseModel<UserCashbackData>) => {
                this.userService.setCashbackData(response.result);
            },
            error: () => {
                this.userService.setCashbackData(null);
            }
        })
    }

    private async handleGetAllowedPages() {
        try {
            const response = await firstValueFrom<ApiResponseModel<UserAllowedPage[]>>(this.getAllowedPages());
            this.userService.setUserAllowedPages(response.result);
            return true;
        } catch {
            return false;
        }
    }

    private async handleMultilogin() {
        try {
            const response = await firstValueFrom<ApiResponseModel<UserMultiloginData[]>>(this.getMultiloginData());
            this.userService.setUserMultiloginData(response.result);
            this.handleGetUserCashback();
        } catch { }
    }

    private async handleTermsOfService(): Promise<boolean> {
        const params = this.getTermsOfUseSwal()

        return await this.notificationService.showConfirm(params).then(async (result) => {
            this.userService.setUserFirstLoginDate(new Date());

            if (!result.isConfirmed)
                return;

            const response = await firstValueFrom<ApiResponseModel<boolean>>(this.saveFirstLogin());

            if (response.result)
                this.userService.setUserAcceptTermsOfUse();

            return response.result;
        });
    }
}
